import React from 'react'
import type { ComponentProps, PartData } from '@enonic/react-components'
import FeaturedCardsPrototype from '/react4xp/components/prototype/FeaturedCards'
import type { FeaturedCard, FeaturedCardsProps } from '/react4xp/components/prototype/FeaturedCards/types'
import { EditorPlaceholder } from '/react4xp/components/shared/EditorPlaceholder'

type ProcessorCard = {
	image?: { src: string; alt?: string }
	label?: string
	title?: string
	href?: string
	external: boolean
}

type FeaturedCardsPartData = {
	overline?: string
	title?: string
	cards?: ProcessorCard[]
	viewAllLink?: { label: string; url: string; isExternal: boolean }
	prevLabel?: string
	nextLabel?: string
	// Emitted by the processor only when `cards` is empty.
	overrideRowCount?: number
	sourceFolderSet?: boolean
}

function toFeaturedCard(card: ProcessorCard): FeaturedCard {
	return {
		image: card.image && { src: card.image.src, alt: card.image.alt || '' },
		label: card.label,
		title: card.title || '',
		// The prototype types `url` as a required string and always emits `<a href={url}>`.
		// A card whose optional Link is empty must still render (I99X-345), and `''` would
		// resolve to the current page, so hand down `undefined` instead: React omits the
		// attribute and the overlay anchor is inert and unfocusable. The cast marks a
		// prop-contract gap that belongs upstream - `url` should be optional in the prototype.
		url: card.href as unknown as string,
		isExternal: card.external,
	}
}

function emptyReason(data: FeaturedCardsPartData): string {
	if (data.overrideRowCount) {
		return `${data.overrideRowCount} override row(s) added, but none has a title, image or label.`
	}
	if (data.sourceFolderSet) {
		return 'The selected source folder has no listable children.'
	}
	return 'No source folder selected and no override cards added.'
}

export const FeaturedCards = ({ meta, data }: ComponentProps<PartData>) => {
	const partData = (data || {}) as FeaturedCardsPartData
	const { overline, title, cards = [], viewAllLink, prevLabel, nextLabel } = partData

	// Keep a card as long as it has something to show. A missing title no longer removes it
	// (I99X-345): `Card`'s full-bleed anchor passes `aria-label={title || undefined}`, and a
	// card without a link now renders no href at all, so there is no unlabelled link to ship.
	const featuredCards = cards.map(toFeaturedCard).filter((card) => !!card.title || !!card.image || !!card.label)

	// Guard on the filtered cards alone. Title / overline / view-all are optional chrome;
	// a zero-card part still paints section + heading in the prototype, so the wrapper
	// must return the editor placeholder (live/preview: nothing) instead of mounting it.
	if (featuredCards.length === 0) {
		return (
			<EditorPlaceholder
				mode={meta?.mode}
				componentClass="featured-cards"
				title="Featured cards"
				reason={emptyReason(partData)}
			/>
		)
	}

	const props: FeaturedCardsProps = {
		overline,
		title,
		cards: featuredCards,
		viewAllLink: viewAllLink && { text: viewAllLink.label, url: viewAllLink.url, isExternal: viewAllLink.isExternal },
		prevLabel,
		nextLabel,
	}

	return <FeaturedCardsPrototype {...props} />
}
